import React, { useEffect, useState } from "react";
import {
  Card,
  Avatar,
  Button,
  Tag,
  Dropdown,
  Pagination,
  Carousel,
  Spin,
  App,
  Modal,
} from "antd";
import {
  VideoCameraOutlined,
  MoreOutlined,
  CalendarOutlined,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { apiGetFollowUps } from "../../api/followUps";
import dayjs from "dayjs";
import { formatTime, getTimeUntilAppointment } from "../../common/utils";
import { apiCancelAppointment } from "../../api/appointments";

const FollowUps = () => {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [followUpsData, setFollowUpsData] = useState([]);
  const [healthCheckData, setHealthCheckData] = useState([]);
  const [activeTab, setActiveTab] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [carouselRef, setCarouselRef] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cancelModalVisible, setCancelModalVisible] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  const pageSize = 4;

  const fetchFollowUpsData = async () => {
    try {
      setLoading(true);

      const params = {};
      if (activeTab === "all") {
        params.status = 1;
      } else if (activeTab === "pending") {
        params.status = 2;
      } else if (activeTab === "completed") {
        params.status = 3;
      }

      const res = await apiGetFollowUps(params);
      setFollowUpsData(res?.data?.followUpConsultations || []);
      setHealthCheckData(res?.data?.healthCheckQuestionnaires || []);
    } catch (error) {
      console.error("Follow-ups fetch error:", error);
      message.error("Không thể tải dữ liệu theo dõi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFollowUpsData();
  }, [activeTab]);

  const paginatedFollowUps = followUpsData.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const chunkHealthChecks = (arr, size) => {
    const chunks = [];
    for (let i = 0; i < arr.length; i += size) {
      chunks.push(arr.slice(i, i + size));
    }
    return chunks;
  };

  const healthCheckChunks = chunkHealthChecks(healthCheckData, 4);

  const getRiskInfo = (level) => {
    const riskMap = {
      1: { color: "gold", text: "Rủi ro thấp" },
      2: { color: "orange", text: "Rủi ro trung bình" },
      3: { color: "red", text: "Rủi ro cao" },
    };
    return riskMap[level] || { color: "default", text: "Không rõ" };
  };

  const handleCancelAppointment = async () => {
    if (!selectedAppointment) return;

    try {
      setCancelling(true);
      await apiCancelAppointment(selectedAppointment.id);
      message.success("Cuộc hẹn đã được hủy thành công");
      setCancelModalVisible(false);
      setSelectedAppointment(null);
      fetchFollowUpsData();
    } catch (error) {
      console.error("Error cancelling appointment:", error);
      message.error("Không thể hủy cuộc hẹn");
    } finally {
      setCancelling(false);
    }
  };

  const actionMenu = (consultation) => ({
    items: [
      {
        key: "video",
        label: "Bắt đầu cuộc gọi video",
        disabled: !consultation.joinVideoCall,
      },
      {
        key: "create",
        label: "Khởi tạo",
        disabled: !consultation.reschedule,
      },
      {
        key: "cancel",
        label: "Hủy bỏ",
        disabled: !consultation.cancelAppointment,
        danger: true,
      },
    ],
    onClick: ({ key }) => handleMenuClick(key, consultation),
  });

  const canJoinCall = (consultation) => {
    if (!consultation?.startTimestamp) return false;

    const startTimeMs = consultation.startTimestamp * 1000;
    const now = Date.now();
    const fifteenMinsBefore = startTimeMs - 15 * 60 * 1000;

    return now >= fifteenMinsBefore && now <= startTimeMs + 60 * 60 * 1000;
  };

  const handleMenuClick = (key, consultation) => {
    switch (key) {
      case "create":
        navigate("/appointments/create", {
          state: {
            patient: {
              id: consultation.id,
              patientId: consultation.patientId,
              name: consultation.patientName,
              profileImage: consultation.patientImage,
            },
          },
        });
        break;
      case "cancel":
        setSelectedAppointment(consultation);
        setCancelModalVisible(true);
        break;
      case "video":
        if (consultation?.id) {
          navigate(`/video-call/${consultation?.id}`);
        } else {
          message.info("Cuộc gọi video sẽ sớm bắt đầu");
        }
        break;
      default:
        break;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-[70rem] mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-gray-800">Theo dõi</h1>
          <p className="text-gray-500 mt-1">
            Lên lịch và quản lý các cuộc hẹn tái khám, đồng thời xem xét các phiếu khảo sát sức khỏe của bệnh nhân
          </p>
        </div>

        <Card
          title="Các buổi tư vấn tiếp theo"
          className="shadow-sm mb-6 rounded-lg"
        >
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <Spin size="large" />
            </div>
          ) : paginatedFollowUps.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">Không có buổi tư vấn tiếp theo</p>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                {paginatedFollowUps.map((consultation) => (
                  <div
                    key={consultation.id}
                    className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center gap-4 min-w-0 flex-shrink">
                      <Avatar size={48} src={consultation?.patientImage}>
                        {!consultation?.patientImage &&
                          consultation.patientName?.charAt(0)?.toUpperCase()}
                      </Avatar>

                      <div className="min-w-0 flex-shrink">
                        <h3 className="font-semibold text-gray-800">
                          {consultation.patientName}
                        </h3>
                        <p className="text-sm text-gray-500 truncate">
                          {consultation.subject}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 flex-shrink-0 ml-4">
                      <span className="text-gray-700 font-medium whitespace-nowrap">
                        {formatTime(
                          consultation.startTimestamp,
                          consultation.endTimestamp
                        )}
                        {getTimeUntilAppointment(consultation.startTimestamp) && (
                          <p className="text-xs text-purple-600 mt-1">
                            {getTimeUntilAppointment(consultation.startTimestamp)}
                          </p>
                        )}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                      <Button
                        type="primary"
                        icon={<VideoCameraOutlined />}
                        className="bg-purple-600 hover:bg-purple-700 border-purple-600"
                        disabled={!canJoinCall(consultation) || !consultation?.joinVideoCall}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMenuClick("video", consultation);
                        }}
                      />
                      <Dropdown
                        menu={actionMenu(consultation)}
                        trigger={["click"]}
                        placement="bottomRight"
                      >
                        <Button
                          icon={<MoreOutlined />}
                          type="text"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </Dropdown>
                    </div>
                  </div>
                ))}
              </div>

              {followUpsData.length > pageSize && (
                <div className="flex justify-end mt-6 pt-4 border-t border-gray-200">
                  <Pagination
                    current={currentPage}
                    total={followUpsData.length}
                    pageSize={pageSize}
                    onChange={(page) => setCurrentPage(page)}
                    showSizeChanger={false}
                    showTotal={(total) => `Tổng cộng ${total} tư vấn`}
                  />
                </div>
              )}
            </>
          )}
        </Card>

        <Card
          title="Phiếu khảo sát sức khỏe hàng tuần"
          className="shadow-sm rounded-lg"
          extra={
            <div className="flex gap-6">
              <Button
                variant="outlined"
                color={activeTab === "all" && "primary"}
                size="medium"
                onClick={() => setActiveTab("all")}
              >
                Tất cả
              </Button>
              <Button
                variant="outlined"
                color={activeTab === "pending" && "primary"}
                size="medium"
                onClick={() => setActiveTab("pending")}
              >
                Đang tạo ngưng
              </Button>
              <Button
                variant="outlined"
                color={activeTab === "completed" && "primary"}
                size="medium"
                onClick={() => setActiveTab("completed")}
              >
                Hoàn thành
              </Button>
            </div>
          }
        >
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <Spin size="large" />
            </div>
          ) : healthCheckData.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">Không có phiếu khảo sát sức khỏe</p>
            </div>
          ) : healthCheckChunks.length === 1 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {healthCheckData.map((questionnaire) => (
                <QuestionnaireCard
                  key={questionnaire.id}
                  questionnaire={questionnaire}
                  navigate={navigate}
                  getRiskInfo={getRiskInfo}
                />
              ))}
            </div>
          ) : (
            <div className="relative">
              <Button
                icon={<LeftOutlined />}
                onClick={() => carouselRef?.prev()}
                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg hover:bg-purple-50 border-purple-200"
                shape="circle"
                size="large"
              />
              <Button
                icon={<RightOutlined />}
                onClick={() => carouselRef?.next()}
                className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg hover:bg-purple-50 border-purple-200"
                shape="circle"
                size="large"
              />

              <Carousel ref={setCarouselRef} dots={true} autoplay={false}>
                {healthCheckChunks.map((chunk, chunkIndex) => (
                  <div key={chunkIndex} className="px-1">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {chunk.map((questionnaire) => (
                        <QuestionnaireCard
                          key={questionnaire.id}
                          questionnaire={questionnaire}
                          navigate={navigate}
                          getRiskInfo={getRiskInfo}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </Carousel>
            </div>
          )}
        </Card>
      </div>

      <Modal
        open={cancelModalVisible}
        onCancel={() => {
          setCancelModalVisible(false);
          setSelectedAppointment(null);
        }}
        footer={null}
        centered
        width={500}
        closable={false}
        className="cancel-appointment-modal"
      >
        <div className="text-center py-4">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Cancel</h2>
          <p className="text-gray-600 mb-6">
            Bạn có chắc chắn muốn hủy cuộc hẹn không?
          </p>

          <div className="flex gap-3 justify-center">
            <Button
              size="large"
              onClick={() => {
                setCancelModalVisible(false);
                setSelectedAppointment(null);
              }}
              className="px-8 min-w-[120px]"
            >
              Không
            </Button>
            <Button
              type="primary"
              size="large"
              onClick={handleCancelAppointment}
              loading={cancelling}
              className="bg-purple-600 hover:bg-purple-700 border-purple-600 px-8 min-w-[120px]"
            >
              Có
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

const QuestionnaireCard = ({ questionnaire, navigate, getRiskInfo }) => {
  const riskInfo = getRiskInfo(questionnaire.riskLevel);

  return (
    <Card className="shadow-sm border border-gray-200 rounded-lg">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <Avatar
            size={48}
            src={questionnaire?.profileImage}
            style={{
              backgroundColor: questionnaire?.profileImage
                ? undefined
                : "#8B5CF6",
            }}
          >
            {!questionnaire?.profileImage &&
              questionnaire.fullName?.charAt(0)?.toUpperCase()}
          </Avatar>
          <div>
            <h3 className="font-semibold text-gray-800">
              {questionnaire.fullName}
            </h3>
            <p className="text-xs text-gray-500">
              Đã gửi:{" "}
              {dayjs(questionnaire.submittedAt * 1000).format(
                "MMM DD, YYYY - h:mm A"
              )}
            </p>
          </div>
        </div>
        <Tag color={riskInfo.color} className="rounded-full">
          {riskInfo.text}
        </Tag>
      </div>

      <div className="mb-4">
        <p className="text-xs text-gray-500 uppercase mb-1">Theo dõi cân nặng</p>
        <p className="text-sm text-gray-700">
          Hiện hành:{" "}
          <span className="font-medium text-gray-900">
            {questionnaire.currentWeightLbs
              ? `${questionnaire.currentWeightLbs} lbs`
              : "N/A"}
          </span>
        </p>
        <p className="text-xs text-gray-600 mt-1">
          {questionnaire.weightChangeDisplay}
        </p>
      </div>

      <div className="mb-4">
        <p className="text-xs text-gray-500 uppercase mb-1">
          Tuân thủ dùng thuốc
        </p>
        <p className="text-sm text-gray-700">
          {questionnaire.medicationAdherence}
        </p>
      </div>

      <div className="flex gap-2">
        <Button
          type="primary"
          className={
            questionnaire.isReviewed
              ? "bg-gray-400 border-gray-400 hover:bg-gray-500 flex-1"
              : "bg-purple-600 hover:bg-purple-700 border-purple-600 flex-1"
          }
          size="large"
          onClick={() =>
            navigate(`/follow-ups/questionnaire/${questionnaire.id}`)
          }
        >
          {questionnaire.isReviewed ? "Đã được xem xét" : "Phiếu khảo sát đánh giá"}
        </Button>
        <Button
          type="default"
          size="large"
          icon={<CalendarOutlined />}
          className="border-gray-300 hover:border-purple-600 hover:text-purple-600"
          onClick={() =>
            questionnaire.appointmentId
              ? navigate(`/appointments/${questionnaire.appointmentId}`)
              : navigate("/appointments/create", {
                  state: {
                    patient: {
                      id: questionnaire.patientId,
                      patientId: questionnaire.patientId,
                      name: questionnaire.fullName,
                      profileImage: questionnaire.profileImage,
                    },
                  },
                })
          }
        />
      </div>
    </Card>
  );
};

export default FollowUps;